'use client';

import { joinClassNames, WithClassnames } from '@vertex-protocol/web-common';
import { useLightweightChartData } from 'client/modules/trading/chart/hooks/useLightweightChartData';
import { LightweightChart } from 'client/modules/trading/chart/LightweightChart';
import { getOrderPriceLinesFromEngineOrders } from 'client/modules/trading/chart/getOrderPriceLinesFromEngineOrders';
import { useSubaccountOpenEngineOrders } from 'client/hooks/query/subaccount/useSubaccountOpenEngineOrders';
import { useMarket } from 'client/hooks/markets/useMarket';
import { getSharedProductMetadata } from 'client/utils/getSharedProductMetadata';
import { useMemo, useState } from 'react';

interface Props extends WithClassnames {
  productId: number | undefined;
}

interface TimeframeOption {
  label: string;
  period: number;
}

const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { label: '1m', period: 60 },
  { label: '5m', period: 300 },
  { label: '15m', period: 900 },
  { label: '30m', period: 1800 },
  { label: '1h', period: 3600 },
  { label: '4h', period: 14400 },
  { label: '1d', period: 86400 },
];

/**
 * Wrapper that replaces TradingView (paid license) with lightweight-charts (MIT).
 * Fetches OHLC candlestick data from the Nado indexer and renders using
 * the active ArCa theme colors. Also shows open limit orders as dashed
 * horizontal price lines (green = buy, red = sell).
 * Includes a timeframe selector and an OHLC data header overlay.
 */
export function TradingViewChart({ className, productId }: Props) {
  const [period, setPeriod] = useState(3600);

  const { data: candles = [] } = useLightweightChartData({ productId, period });
  const { data: openEngineOrders } = useSubaccountOpenEngineOrders();
  const { data: market } = useMarket({ productId });

  const symbol = useMemo(() => {
    if (!market?.metadata) return undefined;
    return getSharedProductMetadata(market.metadata).marketName;
  }, [market]);

  const orderPriceLines = useMemo(() => {
    if (!productId || !openEngineOrders) return [];
    const ordersForProduct = openEngineOrders[productId] ?? [];
    // Default to 'cross' — engine orders from the default subaccount are cross margin
    return getOrderPriceLinesFromEngineOrders(ordersForProduct, 'cross');
  }, [productId, openEngineOrders]);

  const selectedTimeframe = TIMEFRAME_OPTIONS.find((t) => t.period === period);

  return (
    <div
      className={joinClassNames(
        'relative flex h-full w-full flex-col',
        className,
      )}
    >
      {/* Chart fills remaining space */}
      <div className="relative min-h-0 flex-1">
        <LightweightChart
          className="h-full w-full"
          candles={candles}
          productId={productId}
          orderPriceLines={orderPriceLines}
          symbol={symbol}
          period={selectedTimeframe?.label ?? '1h'}
        />
      </div>
      {/* Timeframe selector below the chart */}
      <div className="flex items-center gap-x-1 px-2 py-1">
        {TIMEFRAME_OPTIONS.map(({ label, period: p }) => (
          <button
            key={label}
            onClick={() => setPeriod(p)}
            className={joinClassNames(
              'rounded px-1.5 py-0.5 text-xs font-medium transition-colors',
              p === period
                ? 'text-accent'
                : 'text-text-tertiary hover:text-text-secondary',
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
